/**
 * Isomorphic exception reporting for OneUptime.
 *
 * `captureException` records a sanitised exception on the active OpenTelemetry
 * span (or on a standalone, always-sampled one when none is recording) and
 * emits an error log record, so one failure lands in OneUptime's Exceptions
 * view and its Logs view. It is a safe no-op when no tracer/logger provider is
 * registered — the OTel globals return non-recording spans and a no-op logger.
 *
 * Two properties are load-bearing and covered by tests:
 *
 *  - **Privacy.** Nothing reaches a span or a log record before it has been
 *    through `sanitize`. Messages in this app can carry a Postgres DSN, a form
 *    submitter's email, or a Drizzle `Failed query:` wrapper with inline
 *    literals; stacks can carry request headers. `safeErrorInfo` refuses to
 *    invoke getters and allowlists the error class name.
 *  - **Errors survive a trace ratio of 0.** The span is named
 *    `ERROR_SPAN_NAME`, which `ErrorPreservingSampler` always records, and the
 *    log record is emitted regardless of any trace decision.
 *
 * @module lib/observability/errors
 */

import { SpanStatusCode, trace } from '@opentelemetry/api'
import { logs, SeverityNumber } from '@opentelemetry/api-logs'

import { ERROR_SPAN_NAME } from './otlp'
import { readDataProperty, safeErrorInfo, sanitizeAttributes, sanitizeStack } from './sanitize'

/**
 * Expected, non-actionable conditions. Next.js throws `NEXT_REDIRECT`,
 * `NEXT_NOT_FOUND` and `NEXT_HTTP_ERROR_FALLBACK` as *control flow*; a client
 * that navigates away aborts in-flight requests; a Neon connection can be
 * recycled mid-query. None of these is a code defect, and reporting them would
 * drown the real ones.
 */
const TRANSIENT_MESSAGE_PATTERNS: readonly string[] = [
  'NEXT_REDIRECT',
  'NEXT_NOT_FOUND',
  'NEXT_HTTP_ERROR_FALLBACK',
  'aborted',
  'ResponseAborted',
  'ECONNRESET',
  'EPIPE',
  'socket hang up',
  'Connection terminated',
  'Client has encountered a connection error',
]

function errorText(error: unknown): string {
  const info = safeErrorInfo(error)
  return `${info.name} ${info.message}`
}

/** Next.js stamps its control-flow throws with a `digest` data property. */
function hasNextDigest(error: unknown): boolean {
  const digest = readDataProperty(error, 'digest')?.value
  return typeof digest === 'string' && digest.startsWith('NEXT_')
}

/** True for control-flow and transient conditions that must not be reported. */
export function isTransientError(error: unknown): boolean {
  if (hasNextDigest(error)) return true
  const text = errorText(error)
  return TRANSIENT_MESSAGE_PATTERNS.some((pattern) => text.includes(pattern))
}

/** True for an aborted request or a cancelled operation. */
export function isAbortError(error: unknown): boolean {
  const name = readDataProperty(error, 'name')?.value
  if (name === 'AbortError') return true
  const text = errorText(error)
  return text.includes('AbortError') || text.includes('The operation was aborted')
}

export interface CaptureContext {
  tags?: Record<string, unknown>
  extra?: Record<string, unknown>
}

/** Tags and extras, sanitized and flattened to primitives. */
function toAttributes(context?: CaptureContext): Record<string, string | number | boolean> {
  return {
    ...sanitizeAttributes(context?.tags, 'tag.'),
    ...sanitizeAttributes(context?.extra, 'extra.'),
  }
}

/**
 * Re-entrancy guard: reporting an error must never itself produce an error that
 * is reported. Without this, an exporter that throws inside `emit` would
 * generate the next record to fail on.
 */
let reporting = false
/** Errors already reported, so the same object is not counted twice. */
const reported = new WeakSet<object>()

/**
 * Report one failure. Never throws: this runs in the error path of request
 * handling, where a secondary exception would replace the real one.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  if (reporting) return
  if (error === null || error === undefined) return
  if (isTransientError(error)) return

  reporting = true
  try {
    if (typeof error === 'object' && error !== null) {
      if (reported.has(error)) return
      reported.add(error)
    }

    const info = safeErrorInfo(error)
    const attributes = {
      'exception.type': info.name,
      'exception.message': info.message,
      ...toAttributes(context),
    }

    // A sanitized *copy* is what gets recorded — never the original object,
    // whose `cause`, custom properties or getters could carry a credential.
    const sanitizedException = {
      name: info.name,
      message: info.message,
      stack: sanitizeStack(info.stack),
    }

    const active = trace.getActiveSpan()
    if (active && active.isRecording()) {
      active.recordException(sanitizedException)
      active.setStatus({ code: SpanStatusCode.ERROR })
    } else {
      const span = trace.getTracer('app-errors').startSpan(ERROR_SPAN_NAME, { attributes })
      span.recordException(sanitizedException)
      span.setStatus({ code: SpanStatusCode.ERROR })
      span.end()
    }

    // Also emit a log record so the failure is searchable in Logs and is
    // extracted into Exceptions by OneUptime. This is emitted whatever the
    // trace sampler decided.
    logs.getLogger('app-errors').emit({
      severityNumber: SeverityNumber.ERROR,
      severityText: 'ERROR',
      body: info.message,
      attributes: {
        ...attributes,
        'exception.stacktrace': sanitizedException.stack,
      },
    })
  } catch {
    // Nothing here is allowed to change application behaviour.
  } finally {
    reporting = false
  }
}
